import { DesktopMacOutlined, PlayArrowOutlined, Refresh, StopOutlined, WarningAmber } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import FuelAutoComplete from '@/components/fuel-autocomplete';
import SimplePlayer from '@/components/simple-player/SimplePlayer';
import VNLiveStatusBadge from '@/components/VNLiveStatusBadge/VNLiveStatusBadge';
import VNPortlet from '@/components/VNPortlet/VNPortlet';
import { useProgress } from '@/contexts/ProgressProvider';
import type { LiveProgram } from '@/models/channel';
import type { Input } from '@/models/input';
import {
  isInputEncoderConnected,
  isInputOverallStatusDisconnected,
  isInputOverallStatusLive,
  isInputOverallStatusLiveSessionActive,
} from '@/models/input';
import { deriveStartStopMethodFromLiveProgram } from '@/services/broadcasts2/broadcasts2.service';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';
import { applicationDataSelector } from '@/store/slices/application-data';
import useAppUtils from '@/utils/appUtils';

import {
  idsMatch,
  PREVIEW_MAX_WAIT_LABEL,
  PREVIEW_MAX_WAIT_MS,
  PREVIEW_RETRY_INTERVAL_MS,
  runManualAction,
} from './inputLiveProgramUtils';
import { useInputSourceLiveStatus } from './InputSourceLiveStatusProvider';
import SourceStatusBadge from './SourceStatusBadge';

type LiveProgramDropdownOption = {
  id: string;
  dropdownLabel: string;
  dropdownHtml: string;
};

type PreviewLoadState = 'idle' | 'loading' | 'ready' | 'failed';

type LivePreviewState = {
  jobId: string | null;
  livePreviewUrl: string | null;
};

// Default empty state for an encoder preview stream session.
const buildLivePreviewState = (): LivePreviewState => ({
  jobId: null,
  livePreviewUrl: null,
});

// Escape user-provided text before inserting it into dropdown HTML.
const escapeLiveProgramOptionHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const LIVE_PROGRAM_READINESS_WARNING_ICON =
  '<svg class="input-source-preview__live-program-option-warning-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path></svg>';

const LIVE_PROGRAM_READINESS_READY_ICON =
  '<svg class="input-source-preview__live-program-option-ready-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.75"></circle><path d="M8.5 12.5l2.2 2.2 5.3-5.3" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

// Format a schedule ISO datetime as OU-local time of day for dropdown display.
const formatScheduleDisplayStartTimeOfDay = (startTimeRaw: string, timezone: string): string => {
  const trimmed = startTimeRaw.trim();
  if (!trimmed) {
    return '';
  }

  const converted = DateTime.fromISO(trimmed, { setZone: true }).setZone(timezone);
  if (converted.isValid) {
    return converted.toFormat('h:mm a');
  }

  return trimmed;
};

// Render the readiness warning or ready chip for a dropdown live program option.
const renderLiveProgramReadinessHtml = (program: LiveProgram, isLive: boolean): string => {
  if (isLive) {
    return '';
  }

  if (!(program.channelIds ?? []).length) {
    return `<span class="input-source-preview__live-program-option-warning" title="No destination assigned.">${LIVE_PROGRAM_READINESS_WARNING_ICON}</span>`;
  }

  return `<span class="input-source-preview__live-program-option-ready-chip">${LIVE_PROGRAM_READINESS_READY_ICON}Ready</span>`;
};

// Build the rich HTML label for a live broadcast dropdown option.
const renderLiveProgramDropdownOptionHtml = (
  program: LiveProgram,
  recordingProgramIds: string[],
  timezone: string
): string => {
  const name = program.name?.trim() || 'Untitled';
  const scheduleDisplay = program.metadata?.scheduleDisplay;
  const isLive = Boolean(
    program.id && recordingProgramIds.some((id) => idsMatch(program.id as string, id))
  );
  const livePill = isLive ? '<span class="input-source-preview__live-program-option-pill">Live</span>' : '';
  const readinessHtml = renderLiveProgramReadinessHtml(program, isLive);

  let secondLine = 'Manual start/stop';

  const startStopMethod = deriveStartStopMethodFromLiveProgram(program);
  if (startStopMethod !== 'Manual') {
    const startTimeRaw = scheduleDisplay?.startTime?.toString().trim() ?? '';
    const startTimeDisplay = startTimeRaw ? formatScheduleDisplayStartTimeOfDay(startTimeRaw, timezone) : '';
    const recurrence = scheduleDisplay?.recurrence?.trim() ?? '';
    secondLine = [startTimeDisplay, recurrence].filter(Boolean).join(' ') || 'Scheduled';
  }

  return [
    '<div class="input-source-preview__live-program-option">',
    `<div class="input-source-preview__live-program-option-name"><strong>${escapeLiveProgramOptionHtml(
      name
    )}</strong>${livePill}${readinessHtml}</div>`,
    `<div class="input-source-preview__live-program-option-meta">${escapeLiveProgramOptionHtml(secondLine)}</div>`,
    '</div>',
  ].join('');
};

export type InputSourcePreviewPanelProps = {
  input?: Input | null;
  livePrograms?: LiveProgram[];
  defaultBroadcastId?: string;
};

// Encoder preview player with live broadcast selection and start/stop controls.
const InputSourcePreviewPanel = ({ input, livePrograms = [], defaultBroadcastId = '' }: InputSourcePreviewPanelProps) => {
  const inputId = input?.id;
  const isSingleLiveProgramMode = Boolean(defaultBroadcastId);
  const { liveStatus } = useInputSourceLiveStatus(inputId);
  const { ouData } = useSelector(applicationDataSelector);
  const ouId = ouData?.ouId ?? input?.entityOuId;
  const timezone = ouData?.timezone ?? 'UTC';
  const { alert, vnConfirm } = useAppUtils();
  const { setProgress } = useProgress();
  const [selectedLiveProgramId, setSelectedLiveProgramId] = useState<string | null>(null);
  const [showActionWarning, setShowActionWarning] = useState(false);
  const [previewRequested, setPreviewRequested] = useState(false);
  const [previewLoadState, setPreviewLoadState] = useState<PreviewLoadState>('idle');
  const [livePreviewState, setLivePreviewState] = useState<LivePreviewState>(buildLivePreviewState);
  const selectedLiveProgramRef = useRef<LiveProgramDropdownOption | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const previewSessionRef = useRef(0);
  const isStartingPreviewRef = useRef(false);
  const previewStartStartedAtRef = useRef(0);
  const previewStartRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevOverallStatusRef = useRef<string | undefined>(undefined);
  const autoSelectedForLiveProgramIdRef = useRef('');

  // Clear any pending preview-start retry timer.
  const clearPreviewStartRetry = useCallback(() => {
    if (previewStartRetryTimeoutRef.current) {
      clearTimeout(previewStartRetryTimeoutRef.current);
      previewStartRetryTimeoutRef.current = null;
    }
  }, []);

  // Clear local preview viewer state only — do not stop the shared FFmpeg job.
  const clearLocalPreviewState = useCallback(() => {
    jobIdRef.current = null;
    setLivePreviewState(buildLivePreviewState());
  }, []);

  // Invalidate the current preview session and return UI to the idle state.
  const resetPreviewSession = useCallback(() => {
    clearPreviewStartRetry();
    previewStartStartedAtRef.current = 0;
    previewSessionRef.current += 1;
    isStartingPreviewRef.current = false;
    setPreviewLoadState('idle');
    clearLocalPreviewState();
  }, [clearLocalPreviewState, clearPreviewStartRetry]);

  // Retry preview start until the stream URL is available or the max wait window expires.
  const schedulePreviewStartRetry = useCallback(
    (session: number, startPreview: (force?: boolean, resetWindow?: boolean) => Promise<void>) => {
      if (previewSessionRef.current !== session) {
        return;
      }

      const elapsed = Date.now() - previewStartStartedAtRef.current;
      if (elapsed < PREVIEW_MAX_WAIT_MS) {
        setPreviewLoadState('loading');
        clearPreviewStartRetry();
        previewStartRetryTimeoutRef.current = setTimeout(() => {
          previewStartRetryTimeoutRef.current = null;
          startPreview(true, false).catch(() => {});
        }, PREVIEW_RETRY_INTERVAL_MS);
        return;
      }

      clearPreviewStartRetry();
      previewStartStartedAtRef.current = 0;
      setPreviewLoadState('failed');
    },
    [clearPreviewStartRetry]
  );

  // Request a preview stream from the API and track loading state for the player.
  const startPreview = useCallback(
    async (force = false, resetWindow = force) => {
      if (!inputId || !ouId) {
        return;
      }
      if (!force && (!liveStatus?.overallStatus || isInputOverallStatusDisconnected(liveStatus.overallStatus))) {
        return;
      }
      if (isStartingPreviewRef.current) {
        return;
      }
      if (!force && previewLoadState === 'ready' && livePreviewState.livePreviewUrl) {
        return;
      }

      clearPreviewStartRetry();
      if (resetWindow || previewStartStartedAtRef.current === 0) {
        previewStartStartedAtRef.current = Date.now();
      }

      isStartingPreviewRef.current = true;
      const session = previewSessionRef.current + 1;
      previewSessionRef.current = session;
      setPreviewLoadState('loading');

      try {
        const response = await FuelToolsApiService.livePreviewStart({ inputId, ouId });
        if (previewSessionRef.current !== session) {
          return;
        }

        if (!response?.livePreviewUrl) {
          schedulePreviewStartRetry(session, startPreview);
          return;
        }

        clearPreviewStartRetry();
        previewStartStartedAtRef.current = 0;
        jobIdRef.current = response.jobId;
        setLivePreviewState({
          jobId: response.jobId,
          livePreviewUrl: response.livePreviewUrl,
        });
      } catch {
        if (previewSessionRef.current === session) {
          schedulePreviewStartRetry(session, startPreview);
        }
      } finally {
        if (previewSessionRef.current === session) {
          isStartingPreviewRef.current = false;
        }
      }
    },
    [
      clearPreviewStartRetry,
      inputId,
      livePreviewState.livePreviewUrl,
      liveStatus?.overallStatus,
      ouId,
      previewLoadState,
      schedulePreviewStartRetry,
    ]
  );

  // Tear down the preview session when the encoder disconnects.
  useEffect(() => {
    const currentOverall = liveStatus?.overallStatus;
    const wasPreviewAvailable = Boolean(
      prevOverallStatusRef.current && !isInputOverallStatusDisconnected(prevOverallStatusRef.current)
    );
    const nowDisconnected = isInputOverallStatusDisconnected(currentOverall);

    if (wasPreviewAvailable && nowDisconnected) {
      setPreviewRequested(false);
      resetPreviewSession();
    }

    prevOverallStatusRef.current = currentOverall;
  }, [liveStatus?.overallStatus, resetPreviewSession]);

  // Reset local preview viewer state when the input changes; do not stop the shared FFmpeg job.
  useEffect(() => {
    setPreviewRequested(false);
    setPreviewLoadState('idle');
    setLivePreviewState(buildLivePreviewState());
    jobIdRef.current = null;
    prevOverallStatusRef.current = undefined;
    autoSelectedForLiveProgramIdRef.current = '';

    return () => {
      clearPreviewStartRetry();
      previewSessionRef.current += 1;
      isStartingPreviewRef.current = false;
      jobIdRef.current = null;
    };
  }, [clearPreviewStartRetry, inputId]);

  // Keep the shared live-preview job alive while this viewer is actually watching.
  useEffect(() => {
    if (previewLoadState !== 'ready' || !inputId) {
      return;
    }

    const HEARTBEAT_INTERVAL_MS = 60_000;
    const sendHeartbeat = () => {
      FuelToolsApiService.livePreviewHeartbeat(inputId).catch((err) => {
        console.warn(`livePreviewHeartbeat failed for input ${inputId}`, err);
      });
    };

    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [inputId, previewLoadState]);

  const recordingProgramIds = liveStatus?.liveProgramIds ?? [];

  const liveProgramOptions = useMemo<LiveProgramDropdownOption[]>(
    () =>
      livePrograms
        .filter((program) => program.id)
        .map((program) => ({
          id: program.id as string,
          dropdownLabel: program.name?.trim() || 'Untitled',
          dropdownHtml: renderLiveProgramDropdownOptionHtml(program, recordingProgramIds, timezone),
        })),
    [recordingProgramIds, livePrograms, timezone]
  );

  // When exactly one program is Recording and nothing is selected, select it once.
  // Do not pick an arbitrary first id when multiple programs are live.
  useEffect(() => {
    if (isSingleLiveProgramMode) {
      return;
    }

    if (recordingProgramIds.length !== 1) {
      if (recordingProgramIds.length === 0) {
        autoSelectedForLiveProgramIdRef.current = '';
      }
      return;
    }

    const onlyRecordingId = recordingProgramIds[0];

    if (idsMatch(autoSelectedForLiveProgramIdRef.current, onlyRecordingId)) {
      return;
    }

    const selectedId = selectedLiveProgramRef.current?.id ?? selectedLiveProgramId;
    if (selectedId && idsMatch(selectedId, onlyRecordingId)) {
      autoSelectedForLiveProgramIdRef.current = onlyRecordingId;
      return;
    }

    const liveOption = liveProgramOptions.find((option) => idsMatch(option.id, onlyRecordingId));
    if (!liveOption) {
      return;
    }

    autoSelectedForLiveProgramIdRef.current = onlyRecordingId;
    selectedLiveProgramRef.current = liveOption;
    setSelectedLiveProgramId(liveOption.id);
    setShowActionWarning(false);
  }, [recordingProgramIds, isSingleLiveProgramMode, liveProgramOptions, selectedLiveProgramId]);

  const fixedLiveProgram = defaultBroadcastId ? livePrograms.find((program) => program.id === defaultBroadcastId) ?? null : null;
  const isFixedLiveProgramLive = Boolean(
    fixedLiveProgram?.id && recordingProgramIds.some((id) => idsMatch(fixedLiveProgram.id as string, id))
  );

  const overallStatus = liveStatus?.overallStatus;
  const liveSessionActive = isInputOverallStatusLiveSessionActive(overallStatus);
  const targetStopProgramId = isSingleLiveProgramMode
    ? defaultBroadcastId
    : selectedLiveProgramId ?? selectedLiveProgramRef.current?.id ?? '';
  const isSelectedLiveProgramActive = Boolean(
    targetStopProgramId && recordingProgramIds.some((id) => idsMatch(id, targetStopProgramId))
  );
  // Label Stop whenever the input session is live; enable only for the selected Recording program.
  const showStopMode = liveSessionActive;
  const toggleButtonLabel = showStopMode ? 'Stop' : 'Start';
  // Start: Connected + selected program exists + selected is not already Recording.
  // Stop: selected / go-live program must be Recording (multiple programs may be live).
  const toggleButtonDisabled = showStopMode
    ? !isInputOverallStatusLive(overallStatus) || !isSelectedLiveProgramActive
    : !isInputEncoderConnected(undefined, overallStatus) || !targetStopProgramId || isSelectedLiveProgramActive;

  const isPreviewAvailable = Boolean(overallStatus && !isInputOverallStatusDisconnected(overallStatus));
  const hasPreviewUrl = Boolean(livePreviewState.livePreviewUrl);
  const showVideo = previewLoadState === 'ready' && hasPreviewUrl;
  const showPlayerOverlay = previewLoadState === 'loading' || previewLoadState === 'failed';
  const showConnectedIdle = isPreviewAvailable && previewLoadState === 'idle' && !previewRequested;
  const showDisconnectedPlaceholder = !showConnectedIdle && !showVideo && !showPlayerOverlay;
  const isTrulyLive = isInputOverallStatusLive(overallStatus);
  const isPreviewLoading = previewLoadState === 'loading';
  const playerClassName = [
    'input-source-preview__player',
    showVideo || showPlayerOverlay ? 'input-source-preview__player--active' : '',
    showPlayerOverlay ? 'input-source-preview__player--overlay' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const streamMeta = [
    (input as Input & { metaData?: { resolution?: string; protocol?: string } }).metaData?.resolution,
    input?.protocol ?? (input as Input & { metaData?: { protocol?: string } }).metaData?.protocol,
  ]
    .filter(Boolean)
    .join(' • ');

  const hasLivePrograms = liveProgramOptions.length > 0;

  // Begin preview playback after the user requests it from the idle connected state.
  const handleStartPreview = () => {
    if (!isPreviewAvailable) {
      alert('Wait for the encoder to connect before starting the preview.', 'info');
      return;
    }

    setPreviewRequested(true);
    startPreview(true).catch(() => {});
  };

  // Restart preview playback after a failed load attempt.
  const handleRetryPreview = () => {
    if (!isPreviewAvailable) {
      return;
    }

    setPreviewRequested(true);
    startPreview(true).catch(() => {});
  };

  // Transition preview UI to ready or failed based on player playback status.
  const handlePlaybackReadyWithAlert = (success: boolean) => {
    if (success) {
      setPreviewLoadState('ready');
      return;
    }

    setPreviewLoadState('failed');
    alert('Preview failed to load. The stream may not be available yet — check the encoder status and try again.', 'warning');
  };

  // Confirm and stop the encoder preview without affecting any live broadcast.
  const handleStopPreview = async () => {
    if (!jobIdRef.current) {
      return;
    }

    const isOk = await vnConfirm({
      title: 'Stop Preview',
      message: (
        <Box>
          <Typography variant="body2" gutterBottom>
            Confirm you want to stop the encoder preview.
          </Typography>
          <Typography variant="body2">This stops the preview stream only — it does not stop a live broadcast.</Typography>
        </Box>
      ),
      confirmText: 'Stop Preview',
      cancelText: 'Cancel',
    });

    if (!isOk) {
      return;
    }

    setPreviewRequested(false);
    resetPreviewSession();
  };

  // Keep dropdown selection state in sync with the user's live broadcast choice.
  const handleLiveProgramChange = (option: LiveProgramDropdownOption | null) => {
    selectedLiveProgramRef.current = option;
    setSelectedLiveProgramId(option?.id ?? null);
    if (option) {
      setShowActionWarning(false);
    }
  };

  // Start or stop the selected live broadcast after user confirmation.
  const handleToggleLive = async () => {
    if (!input?.id) {
      return;
    }

    const resolveSelectedLiveProgram = (): LiveProgram | null => {
      if (isSingleLiveProgramMode && fixedLiveProgram) {
        return fixedLiveProgram;
      }

      const selectedId = selectedLiveProgramRef.current?.id ?? selectedLiveProgramId;
      if (!selectedId) {
        return null;
      }

      return livePrograms.find((program) => program.id === selectedId) ?? null;
    };

    const resolveStopLiveProgram = (): LiveProgram | null => {
      const program = resolveSelectedLiveProgram();
      if (program?.id) {
        return program;
      }

      // Go-live / selection id may be Recording but not present in the loaded program list.
      if (targetStopProgramId && isSelectedLiveProgramActive) {
        return {
          id: targetStopProgramId,
          name: 'Live program',
          description: '',
        };
      }

      return null;
    };

    if (!showStopMode) {
      const program = resolveSelectedLiveProgram();
      if (!program?.id) {
        setShowActionWarning(true);
        alert('Select a live program before starting the stream.', 'warning');
        return;
      }

      setShowActionWarning(false);

      const isOk = await vnConfirm({
        title: 'Confirm Start',
        message: (
          <Box>
            <Typography variant="body2" gutterBottom>
              Confirm you want to start this live program.
            </Typography>
            <Typography variant="body2">Streaming may take up to {PREVIEW_MAX_WAIT_LABEL} to begin.</Typography>
          </Box>
        ),
        confirmText: 'Start',
        cancelText: 'Cancel',
      });

      if (!isOk) {
        return;
      }

      try {
        setProgress(true, 'Starting live broadcast...');
        const response = (await runManualAction({
          liveProgramId: program.id as string,
          liveProgramName: program.name?.trim() || 'Untitled',
          inputDetails: input,
          actionType: 'Start',
        })) as { message?: string } | null;

        if (response?.message) {
          alert('Manual recording will start in a moment.');
        }
      } catch {
        alert('Failed to start live program.', 'error');
      } finally {
        setProgress(false);
      }

      return;
    }

    const program = resolveStopLiveProgram();
    if (!program?.id) {
      alert('Unable to determine which live program to stop.', 'error');
      return;
    }

    const isOk = await vnConfirm({
      title: 'Confirm Stop',
      message: (
        <Box>
          <Typography variant="body2" gutterBottom>
            Confirm you want to stop this live program&apos;s stream.
          </Typography>
          <Typography variant="body2">This will stop streaming and recording into the asset library.</Typography>
        </Box>
      ),
      confirmText: 'Stop',
      cancelText: 'Cancel',
    });

    if (!isOk) {
      return;
    }

    try {
      setProgress(true, 'Stopping live broadcast...');
      await runManualAction({
        liveProgramId: program.id as string,
        liveProgramName: program.name?.trim() || 'Untitled',
        inputDetails: input,
        actionType: 'End',
      });
    } catch {
      alert('Failed to stop live program.', 'error');
    } finally {
      setProgress(false);
    }
  };

  if (!input?.id) {
    return null;
  }

  return (
    <VNPortlet title="Encoder Preview" className="input-source-detail__preview-portlet input-source-preview">
      <div className="input-source-preview__card">
        <div className={playerClassName}>
          {showDisconnectedPlaceholder ? (
            <div className="input-source-preview__placeholder">
              <div className="input-source-preview__placeholder-visual">
                <DesktopMacOutlined className="input-source-preview__placeholder-monitor" aria-hidden="true" />
                <button
                  type="button"
                  className="input-source-preview__placeholder-play"
                  onClick={handleStartPreview}
                  disabled={!isPreviewAvailable}
                  aria-label="Start preview"
                >
                  <PlayArrowOutlined />
                </button>
              </div>
              <p className="input-source-preview__placeholder-title">No Signal — Encoder Disconnected</p>
              <p className="input-source-preview__placeholder-text">Connect your encoder to see a live preview</p>
            </div>
          ) : null}

          {showConnectedIdle ? (
            <div className="input-source-preview__connected-idle">
              <div className="input-source-preview__signal">
                <span className="input-source-preview__signal-dot" />
                <span>Live Signal Active</span>
              </div>
              <button
                type="button"
                className="input-source-preview__placeholder-play input-source-preview__placeholder-play--interactive"
                onClick={handleStartPreview}
                aria-label="Start preview"
              >
                <PlayArrowOutlined />
              </button>
              {streamMeta ? <span className="input-source-preview__stream-meta">{streamMeta}</span> : null}
            </div>
          ) : null}

          {showPlayerOverlay ? (
            <div className="input-source-preview__player-overlay">
              {isPreviewLoading ? (
                <CircularProgress size={50} thickness={4} className="input-source-preview__overlay-spinner" />
              ) : null}
              <Typography variant="h6" className="input-source-preview__overlay-message">
                {previewLoadState === 'failed'
                  ? 'Preview failed to load. The stream may not be available yet — check the encoder status and try again.'
                  : `Preview may take up to ${PREVIEW_MAX_WAIT_LABEL} to load.`}
              </Typography>
              {isPreviewLoading ? (
                <Typography variant="body2" className="input-source-preview__overlay-hint">
                  Waiting for the encoder preview stream to load.
                </Typography>
              ) : null}
              {previewLoadState === 'failed' ? (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Refresh />}
                  onClick={handleRetryPreview}
                  className="input-source-preview__overlay-retry"
                >
                  Retry preview
                </Button>
              ) : null}
            </div>
          ) : null}

          {isTrulyLive && previewLoadState === 'ready' ? <VNLiveStatusBadge overlay /> : null}

          {(previewLoadState === 'loading' || previewLoadState === 'ready') && livePreviewState.livePreviewUrl ? (
            <SimplePlayer
              key={livePreviewState.jobId ?? 'preview'}
              videoId={`input-source-preview-${livePreviewState.jobId}`}
              srcUrl={livePreviewState.livePreviewUrl}
              autoPlay
              muted
              hidden={!showVideo}
              maxWaitMs={PREVIEW_MAX_WAIT_MS}
              onPlaybackReady={handlePlaybackReadyWithAlert}
            />
          ) : null}

          {showVideo ? (
            <div className="input-source-preview__player-hover-controls">
              <button
                type="button"
                className="input-source-preview__placeholder-play input-source-preview__preview-stop"
                onClick={() => {
                  handleStopPreview().catch(() => {});
                }}
                aria-label="Stop preview"
              >
                <StopOutlined />
              </button>
            </div>
          ) : null}
        </div>

        <div className="input-source-preview__control-footer">
          {isSingleLiveProgramMode ? (
            <div className="input-source-preview__broadcast-static">
              <span className="input-source-preview__broadcast-static-label">Live Broadcast</span>
              <span className="input-source-preview__broadcast-static-name-row">
                <span className="input-source-preview__broadcast-static-name">
                  {fixedLiveProgram?.name ?? defaultBroadcastId}
                </span>
                {isFixedLiveProgramLive ? <span className="input-source-preview__live-program-option-pill">Live</span> : null}
              </span>
            </div>
          ) : (
            <div className="input-source-preview__broadcast-select">
              <FuelAutoComplete
                key={`${input.id}-${selectedLiveProgramId ?? 'none'}`}
                id="input-source-broadcast-select"
                items={liveProgramOptions}
                itemText="dropdownLabel"
                itemHtml="dropdownHtml"
                itemKey="id"
                labelText=""
                placeHolderText={hasLivePrograms ? 'Select a live broadcast' : 'No live programs configured'}
                multiSelect={false}
                disabled={false}
                defaultValue={
                  selectedLiveProgramId ? liveProgramOptions.find((option) => option.id === selectedLiveProgramId) ?? null : null
                }
                onChange={handleLiveProgramChange}
              />
            </div>
          )}

          <div className="input-source-preview__actions">
            {liveSessionActive ? <SourceStatusBadge status={overallStatus} /> : null}
            <Button
              variant="contained"
              color={showStopMode ? 'error' : 'success'}
              onClick={handleToggleLive}
              disabled={toggleButtonDisabled}
              startIcon={showActionWarning ? <WarningAmber fontSize="small" /> : undefined}
              className="input-source-preview__toggle-btn"
              title={toggleButtonLabel}
            >
              {toggleButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </VNPortlet>
  );
};

export default InputSourcePreviewPanel;
